import { AjaxRequestParams, AjaxResponse, LoadingQueueItem } from "./AjaxRequestParams";
import { loadingQueueHandler } from "./loadingQueueHandler";

export const useAjax = () => {
	const { addToQueue, handleQueueItemFinished } = loadingQueueHandler();

	const ajaxJsonRequest = async <T>(data: AjaxRequestParams): Promise<AjaxResponse<T>> => {
		const result = await ajaxRequest(data);

		try {
			return {
				...result,
				data: JSON.parse(result.data) as T,
			};
		} catch (e) {
			return {
				data: undefined,
				statusCode: result?.statusCode,
				statusText: result?.statusText,
				redirectionUrl: result?.redirectionUrl,
				isResponseOk: result?.isResponseOk,
			};
		}
	};

	const sanitizeParams = (data: AjaxRequestParams) => {
		const url = data.url || data.form.attributes.getNamedItem("action").value;
		const method = data.method || "POST";

		return { url, method };
	};

	const createFormBody = (data: any, form = new FormData(), parentKey = "") => {
		const isObject = (val: any): val is Record<string, any> =>
			typeof val === "object" && val !== null && !(val instanceof Date) && !(val instanceof File);

		if (data === null || data === undefined) return form;

		if (Array.isArray(data)) {
			data.forEach((item, index) => {
				const key = parentKey ? `${parentKey}[${index}]` : `[${index}]`;
				createFormBody(item, form, key);
			});
		} else if (isObject(data)) {
			Object.keys(data).forEach((key) => {
				const fullKey = parentKey ? `${parentKey}.${key}` : key;
				createFormBody(data[key], form, fullKey);
			});
		} else if (parentKey) form.append(parentKey, String(data));

		return form;
	};

	const prepareFetchOptions = (data: {
		abortSignal: AbortSignal;
		method: "GET" | "POST" | "DELETE" | "PUT" | "PATCH";
		request: AjaxRequestParams;
	}) => {
		const fetchOptions: RequestInit = {
			credentials: data.request.includeCredentials ? "include" : "same-origin",
			method: data.method,
			signal: data.abortSignal,
			// Some calls (e.g. breakdown) could be cached, this leaves the site in an incorrect state after the user returns to a page with the back button of the browser.
			cache: "no-cache",
		};

		if (data.request.isJsonData) {
			(fetchOptions as any).headers = {
				"X-Requested-With": "XMLHttpRequest",
				"Content-Type": "application/json",
			};
		}

		if (data.method === "POST" || data.method === "DELETE" || data.method === "PUT") {
			fetchOptions.headers = { ...(fetchOptions.headers || {}) };

			fetchOptions.body = data.request.isJsonData
				? JSON.stringify(data.request.body)
				: createFormBody(data.request.body);
		}

		if (data.request.noCors) fetchOptions.mode = "no-cors";

		return fetchOptions;
	};

	const handleResponse = async (data: {
		queueItem: LoadingQueueItem;
		request: AjaxRequestParams;
		response: Response;
	}): Promise<AjaxResponse<string>> => {
		const result = await data.response.text();

		const callback =
			typeof data.request.onAllLoadingFinished === "function"
				? () => data.request.onAllLoadingFinished(result)
				: undefined;

		handleQueueItemFinished(data.queueItem.id, data.response.ok, callback);

		const shouldHandleResponseCode = typeof data.request.onResponseCode?.[data.response.status] === "function";

		if (shouldHandleResponseCode) data.request.onResponseCode[data.response.status](result);

		if (shouldHandleResponseCode || data.response.ok || data.request.forceReturnResultOnError) {
			return {
				data: result,
				statusCode: data.response.status,
				statusText: data.response.statusText,
				redirectionUrl: data.response.redirected ? data.response.url : "",
				isResponseOk: data.response.ok,
			};
		}

		alert("Error. " + (result as any)?.message || data.response.statusText);

		return undefined;
	};

	const ajaxRequest = async (request: AjaxRequestParams): Promise<AjaxResponse<string>> => {
		if (!request.form && !request.url) throw new Error("AJAX request invalid.");

		const { url, method } = sanitizeParams(request);

		const queueItem = addToQueue(url, request.nonCancellable);

		try {
			const fetchOptions = prepareFetchOptions({
				abortSignal: queueItem.abortController.signal,
				method,
				request,
			});

			const response = await fetch(url, fetchOptions);

			return handleResponse({ queueItem, request, response });
		} catch (e: any) {
			alert("Error. " + e.message);
		}

		return undefined;
	};

	return { ajaxJsonRequest, ajaxRequest };
};
